/**
 * Report completeness (13.2-02): which report fields an incident still lacks.
 *
 * One definition, shared by 13.2-03's triage gate ("Complete the report first: …", D-13.2-P-4) and 13.2-04's
 * `to-complete` lens, so the two can never disagree. Pure: no I/O, no clock.
 *
 * A column is missing when it is an Incidents lodge column (FIELDSET §3.2.1 as amended by 13.2-01), its `lodge` rule
 * applies to this row (`always`, or a conditional that holds on the row's own values), and its value is blank.
 * Columns the Care Manager cannot fill through `complete` are never named (`ReportedByEmail`, the provenance column:
 * an intake stub whose reporter has no ShiftCare email would otherwise be stuck at triage for good).
 */

import { REGISTER_FIELDS, verbColumns, type FieldSpec } from '../config/fieldset/registerFields';
import type { RecordRow, RequiredRule } from '../types/records';

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

function textOf(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value === null || value === undefined ? '' : String(value);
}

/** Does a `lodge` rule apply to this row? `always`, or a conditional whose condition holds on the row's own values. */
function applies(rule: RequiredRule | undefined, row: RecordRow): boolean {
  if (rule === 'always') return true;
  if (rule === undefined || rule === 'optional') return false;
  const v = textOf(row.fields[rule.when]);
  return 'in' in rule ? rule.in.includes(v) : !rule.notIn.includes(v);
}

const LODGE: ReadonlySet<string> = new Set(verbColumns('incidents', 'lodge'));
/** What a Care Manager can complete after lodge (D-13.2-P-3). */
const COMPLETABLE: ReadonlySet<string> = new Set(verbColumns('incidents', 'complete'));

/**
 * 13.2-04: every column `missingLodgeColumns` reads on an Incident: each column it can name, then each column a
 * conditional rule reads (`HarmKind`, `WorkerInjured`, …), in field-set order. Today's list read takes these
 * (`recordQuery.WAITING_READS`), so the lens answers from the list read exactly as from the whole record.
 */
export const COMPLETENESS_READS: readonly string[] = (() => {
  const named = REGISTER_FIELDS.incidents.filter((spec) => {
    const rule = spec.required.lodge;
    return LODGE.has(spec.name) && COMPLETABLE.has(spec.name) && rule !== undefined && rule !== 'optional';
  });
  const conditions = named.flatMap((spec) => {
    const rule = spec.required.lodge;
    return typeof rule === 'object' ? [rule.when] : [];
  });
  return [...new Set([...named.map((spec) => spec.name), ...conditions])];
})();

/** The report columns this incident still needs, in field-set order. `[]` on every other register. */
export function missingLodgeColumns(row: RecordRow): FieldSpec[] {
  if (row.register !== 'incidents') return [];
  return REGISTER_FIELDS.incidents.filter(
    (spec) =>
      LODGE.has(spec.name) &&
      COMPLETABLE.has(spec.name) &&
      applies(spec.required.lodge, row) &&
      isBlank(row.fields[spec.name]),
  );
}

/** The columns the triage verb writes itself (the client id, the injured worker's name, the contact details). */
const TRIAGE: ReadonlySet<string> = new Set(verbColumns('incidents', 'triage'));

/**
 * 13.2-04: the missing report columns that only `complete` can add, because triage does not write them. Non-empty
 * exactly when every triage save is refused `report-incomplete`: the gate reads `missingLodgeColumns` on the record
 * as it will be after the save, and a triage save can add its own columns (a missing client id is triage's to add,
 * and its own rules require it). The `to-complete` lens reads this, so the lens and the gate never disagree.
 */
export function columnsToComplete(row: RecordRow): FieldSpec[] {
  return missingLodgeColumns(row).filter((spec) => !TRIAGE.has(spec.name));
}
